/** @jsx jsx */
import { jsx } from '@emotion/react'
import { css } from '@emotion/react/macro'
import React, { SyntheticEvent, KeyboardEvent } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Link, NavLink } from 'react-router-dom'

import { getFontColor, getFocusColor } from '../../../lib/colorUtils'
import { srOnlyCss, centerBlockCss, pullRightCss } from '../../../lib/styles/cssStyles'
import {
  alternateGray,
  baseColor,
  disabledIconColor,
  gray,
  hoverColor,
  primaryTextColor,
  secondaryGrayColor
} from '../../../lib/styles/colors'
import useTheme from '../../../lib/hooks/useTheme'
import { buttonTextCss, tertiaryBodyCss } from '../../../lib/styles/typography'
import { ButtonType, ButtonVariant } from './types'
import Ripple from './Ripple'
import useRipple from '../../../lib/hooks/useRipple'

export enum Target {
  Blank = '_blank',
  Parent = '_parent',
  Self = '_self',
  Top = '_top'
}

export type ButtonProps = {
  /** Identifies the element (or elements) whose contents or presence are controlled by the button. */
  ariaControls?: string
  /** Indicates the button has a popup, e.g. for menus */
  ariaHasPopup?: boolean
  /** ARIA compliant label */
  ariaLabel?: string
  /** ARIA compliant label */
  ariaSelected?: boolean
  /** Button Color */
  color?: string
  /** Button Dark Color */
  colorDark?: string
  /** Center within a block element */
  center?: boolean
  /** Children nodes */
  children?: React.ReactNode
  /** Optional Class Name */
  className?: string
  /** Disable the Button */
  disabled?: boolean
  /** Background color of button when disabled */
  disabledColor?: string
  /** Toggle focus ripple (default: true) */
  displayFocusRipple?: boolean
  /** Icon to show at the end */
  endIcon?: React.ReactNode
  /** NavLink prop -- used for '/' link  https://github.com/ReactTraining/react-router/issues/5118 */
  exact?: boolean
  /** Applies aria-expanded to the element */
  expanded?: boolean
  /** Force Button to take up full height when in rendered as an anchor */
  fullHeightAnchor?: boolean
  /** When used signifies that the button will be an anchor element */
  href?: string
  /** Assigns an id attribute to the button tag for Accessibility */
  id?: string
  /** Used for focus/cursor handling (useFocus hook) */
  inputRef?: any
  /** Override to indicate a button is active if necessary */
  isActive?: boolean
  /** If the button links to an external source */
  isExternalLink?: boolean
  /** Indicates the button is within a Nav element */
  isNav?: boolean
  /** Click event callback */
  onClick?: (event: SyntheticEvent<HTMLElement>) => void
  /** Key Down event Callback */
  onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void
  /** Key Press event callback */
  onKeyPress?: (event: KeyboardEvent<HTMLElement>) => void
  /** Focus/cursor handling callbacks */
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  /** Pull/float the element to the right */
  pullRight?: boolean
  /** Assigns a role attribute to the button tag for Accessibility */
  role?: string
  /** Indicates if a button is marked as selected */
  selected?: boolean
  /** Indicates if a button is the small version, for modals */
  small?: boolean
  /** Indicates the button is screen-reader only */
  srOnly?: boolean
  /** Screen-reader only label */
  srText?: string
  /** Icon to show at the start */
  startIcon?: React.ReactNode
  /** Used to toggle whether button is focusable for Accessibility */
  tabIndex?: number
  /** Paired with href. Specifies where to open the linked document. */
  target?: Target
  /** Button type. */
  type?: ButtonType
  /** Variant style */
  variant?: ButtonVariant
  /** Optional value for toggle button */
  value?: string
  /** Indicates button should use "wide" style */
  wide?: boolean
}

const defaultProps: ButtonProps = {
  ariaControls: undefined,
  ariaHasPopup: undefined,
  ariaLabel: undefined,
  ariaSelected: undefined,
  color: undefined,
  colorDark: undefined,
  center: false,
  children: undefined,
  className: '',
  disabled: false,
  disabledColor: '#777',
  displayFocusRipple: true,
  endIcon: undefined,
  exact: false,
  expanded: undefined,
  fullHeightAnchor: false,
  href: '',
  id: '',
  inputRef: undefined,
  isActive: false,
  isExternalLink: false,
  isNav: false,
  onClick: undefined,
  onKeyDown: undefined,
  onKeyPress: undefined,
  onMouseEnter: undefined,
  onMouseLeave: undefined,
  pullRight: false,
  role: undefined,
  selected: false,
  small: false,
  srOnly: false,
  srText: '',
  startIcon: undefined,
  tabIndex: undefined,
  target: undefined,
  type: ButtonType.Button,
  variant: undefined,
  value: '',
  wide: false
}

const labelCss = css`
  align-items: inherit;
  display: inherit;
  justify-content: inherit;
  width: 100%;
`
const rippleContainerCss = css`
  overflow: hidden;
  pointer-events: none;
  position: absolute;
  z-index: 0;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  border-radius: inherit;
`
const startIconCss = css`
  display: inherit;
  margin-right: 8px;
  margin-left: 8px;

  & .MuiSvgIcon-root {
    font-size: 20px;
  }
`
const endIconCss = css`
  display: inherit;
  margin-right: 8px;
  margin-left: 8px;

  & .MuiSvgIcon-root {
    font-size: 20px;
  }
`

/** Largely based on Material-UI Button/ButtonBase/TouchRipple/Ripple */

const Button = ({
  ariaControls,
  ariaHasPopup,
  ariaLabel,
  ariaSelected,
  color,
  center,
  children,
  className,
  disabled,
  disabledColor,
  displayFocusRipple,
  endIcon,
  exact,
  expanded,
  fullHeightAnchor,
  href,
  id,
  inputRef,
  isActive,
  isExternalLink,
  isNav,
  onClick,
  onKeyDown,
  onKeyPress,
  onMouseEnter,
  onMouseLeave,
  pullRight,
  role,
  selected,
  small,
  srOnly,
  srText,
  startIcon,
  tabIndex,
  target,
  type,
  value,
  variant,
  wide
}: ButtonProps & typeof defaultProps) => {
  const container = React.useRef<HTMLSpanElement>(null)
  const [ripples, focusRipple, { onTapStart, onTapEnd, onFocus, onBlur }] = useRipple(
    container,
    displayFocusRipple
  )
  const { primaryColor } = useTheme()
  const primaryButtonColor = color ?? primaryColor!
  const defaultColor = color ?? baseColor
  const pathname = window?.location?.pathname

  const minWidthCss = css`
    min-width: 64px;
  `
  const motionWrapperCss = css`
    display: flex;
    height: 100%;
  `

  const wideStyle = css`
    border-radius: 48px;
    min-width: 300px;
    ${href ? null : 'padding: 14px 16px;'}
  `

  const defaultStyle = css`
    ${buttonTextCss}
    align-items: center;
    background-color: transparent;
    border-radius: 4px;
    border: 0;
    color: ${defaultColor};
    cursor: pointer;
    display: inline-flex;
    justify-content: center;
    margin: 0;
    outline: 0;
    padding: 6px 16px;
    position: relative;
    text-decoration: none;
    user-select: none;
    vertical-align: middle;
    white-space: pre;

    ${variant !== ButtonVariant.Icon && minWidthCss}

    transition: background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
      box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
      border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;

    &:hover {
      text-decoration: 'none';
      background-color: ${getFocusColor(defaultColor, variant)};
      /* Reset on touch devices, it doesn't add specificity */
      @media (hover: none) {
        background-color: 'transparent';
      }
      &[disabled] {
        background-color: 'transparent';
      }
    }

    &[disabled] {
      cursor: not-allowed;
      color: ${disabledColor};
    }
  `
  const primaryHoverColor = getFocusColor(primaryButtonColor, variant, isNav)
  const primaryFontColor = getFontColor(primaryHoverColor)
  const primaryVariantCss = css`
    background-color: ${primaryButtonColor};
    color: ${getFontColor(primaryButtonColor || '')};
    font-weight: 700;
    height: ${small ? '34px' : '44px'};
    border-radius: 42px;
    box-shadow: 0px 1px 10px rgba(0, 0, 0, 0.2), 0px 4px 5px rgba(0, 0, 0, 0.12),
      0px 2px 4px rgba(0, 0, 0, 0.14);
    min-width: 100px;
    padding: 0;

    &:hover {
      background-color: ${primaryHoverColor};
      color: ${primaryFontColor};
      box-shadow: 0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14),
        0px 1px 10px 0px rgba(0, 0, 0, 0.12);
      /* Reset on touch devices, it doesn't add specificity */
      box-shadow: 0px 1px 10px rgba(0, 0, 0, 0.2), 0px 4px 5px rgba(0, 0, 0, 0.12),
        0px 2px 4px rgba(0, 0, 0, 0.14);
    }

    &[disabled] {
      background-color: ${secondaryGrayColor};
      color: ${disabledIconColor};
    }
  `

  const primaryLabelCss = css`
    ${labelCss}
    padding-left: ${startIcon ? '0' : '24px'};
    padding-right: ${endIcon ? '0' : '24px'};
  `

  const iconCss = css`
    text-align: center;
    flex: 0 0 auto;
    font-size: 1.5em;
    overflow: visible; // Explicitly set the default value to solve a bug on IE 11.
  `

  const iconVariantCss = css`
    ${iconCss}
    background-color: transparent;
    color: ${defaultColor};
    border-radius: 50%;
    ${!isNav && 'padding: 12px;'}
    transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    &:hover {
      background-color: ${getFocusColor(defaultColor, variant, isNav)};
      // Reset on touch devices; it doesn't add specificity
      @media (hover: none) {
        background-color: transparent;
      }
    }
    &[disabled] {
      background-color: transparent;
      &:hover {
        background-color: ${secondaryGrayColor};
      }
    }
  `

  const secondaryVariantCss = css`
    font-weight: 700;
  `

  const outlineVariantCss = css`
    color: ${primaryTextColor};
    font-weight: 700;
    border-radius: 42px;
    border: 1px solid ${primaryTextColor};
  `

  const toggleVariantCss = css`
    ${iconCss}
    background-color: ${selected ? baseColor : '#FFFFFF'};
    min-width: unset;
    padding-left: 8px;
    padding-right: 8px;
    color: ${selected ? '#FFFFFF' : baseColor};

    &:not(:last-child) {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }

    &:not(:first-of-type) {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      border-left: 1px solid ${gray};
    }

    &:hover {
      background-color: ${selected ? baseColor : hoverColor};
    }
  `

  const backVariantCss = css`
    ${tertiaryBodyCss}
    color: ${alternateGray};
    margin-left: -4px;
    text-transform: none;
    padding-left: 0;

    &:focus,
    &:hover {
      background-color: ${hoverColor};
    }
  `

  const getVariant = () => {
    switch (variant) {
      case ButtonVariant.Primary:
        return primaryVariantCss
      case ButtonVariant.Icon:
        return iconVariantCss
      case ButtonVariant.Secondary:
        return secondaryVariantCss
      case ButtonVariant.Toggle:
        return toggleVariantCss
      case ButtonVariant.Back:
        return backVariantCss
      case ButtonVariant.Outline:
        return outlineVariantCss
      default:
        return null
    }
  }
  const btnStyle = [
    defaultStyle,
    getVariant(),
    wide && wideStyle,
    center && centerBlockCss,
    srOnly && srOnlyCss,
    pullRight && pullRightCss
  ]
  const sr = srText ? <span css={srOnlyCss}>{srText}</span> : null

  const rippleContainer = (
    <span css={rippleContainerCss} ref={container}>
      <AnimatePresence>
        {ripples.map((r) => (
          <Ripple key={r.key} pulsate={r.pulsate} size={r.size} x={r.x} y={r.y} />
        ))}
        {focusRipple && (
          <Ripple
            key={focusRipple.key}
            pulsate={focusRipple.pulsate}
            size={focusRipple.size}
            x={focusRipple.x}
            y={focusRipple.y}
          />
        )}
      </AnimatePresence>
    </span>
  )

  const startIconEl = startIcon && <span css={startIconCss}>{startIcon}</span>
  const endIconEl = endIcon && <span css={endIconCss}>{endIcon}</span>

  const inner = (
    <React.Fragment>
      {sr}
      {startIconEl}
      <span css={variant === ButtonVariant.Primary ? primaryLabelCss : labelCss}>{children}</span>
      {endIconEl}
      {rippleContainer}
    </React.Fragment>
  )

  const commonProps = {
    'aria-label': ariaLabel || srText,
    className,
    onBlur,
    onClick,
    onFocus,
    onKeyDown,
    onKeyPress,
    value,
    rel: target === '_blank' ? 'noreferrer' : undefined,
    target
  }

  /** NavLink doesn't support sub-paths well.  Add some extra
   * checking to make sure we're setting active nav buttons properly.
   */
  const getIsActive = () =>
    isActive || (href && href === pathname) || (href && pathname.startsWith(href)) || false

  if (href && isNav) {
    if (isExternalLink) {
      return (
        <motion.span
          css={fullHeightAnchor && motionWrapperCss}
          onTap={onTapEnd}
          onTapStart={onTapStart}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          <a {...commonProps} aria-label={ariaLabel} css={btnStyle} href={href}>
            {inner}
          </a>
        </motion.span>
      )
    }

    const navBackground = getFocusColor(primaryButtonColor, undefined, isNav)
    const navFontColor = getFontColor(navBackground)
    return (
      <motion.span
        css={fullHeightAnchor && motionWrapperCss}
        onTap={onTapEnd}
        onTapStart={onTapStart}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <NavLink
          {...commonProps}
          activeStyle={{
            background: navBackground,
            color: navFontColor,
            opacity: '100%'
          }}
          isActive={getIsActive}
          css={btnStyle}
          exact={exact}
          to={href}
        >
          {inner}
        </NavLink>
      </motion.span>
    )
  }

  if (href) {
    return (
      <motion.span
        css={fullHeightAnchor && motionWrapperCss}
        onTap={onTapEnd}
        onTapStart={onTapStart}
      >
        <Link
          {...commonProps}
          aria-controls={ariaControls}
          aria-selected={ariaSelected}
          css={btnStyle}
          id={id}
          role={role}
          to={href}
        >
          {inner}
        </Link>
      </motion.span>
    )
  }

  return (
    <motion.button
      {...commonProps}
      aria-controls={ariaControls}
      aria-disabled={disabled}
      aria-expanded={expanded}
      aria-haspopup={ariaHasPopup}
      aria-selected={ariaSelected}
      css={btnStyle}
      disabled={disabled}
      id={id}
      onTap={onTapEnd}
      onTapStart={onTapStart}
      ref={inputRef}
      role={role}
      tabIndex={tabIndex}
      type={type}
    >
      {inner}
    </motion.button>
  )
}

Button.defaultProps = defaultProps

export default Button
